import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Link,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { client } from "./apolloClient";
import styled from "styled-components";
import FilesPage from "./pages/FilesPage";
import NotFoundPage from "./pages/NotFoundPage";
import MagicLinkAuthPage from "./pages/MagicLinkAuthPage";
import RequestMagicLinkPage from "./pages/RequestMagicLinkPage";
import LoginHistoryPage from "./pages/LoginHistoryPage";
import { ThemeProvider } from "styled-components";
import { THEME } from "./styles/themes";
import { GlobalStyle } from "./styles/globals";
import { semanticColors } from "./styles/latitudeTheme";
import AdminPage from "./admin/pages/AdminPage";
import ProtectedRoute from "./components/ProtectedRoute";
import { getUserData } from "./util/userAuth";
import { NotificationProvider } from "./contexts/NotificationContext";
import { SessionExpiredProvider } from "./contexts/SessionExpiredContext";
import { SessionWarningProvider } from "./contexts/SessionWarningContext";
import {
  clearAuthStateOnLogout,
  MAGIC_LINK_AUTH_TYPE_KEY,
} from "./util/authCleanup";
import { Logo } from "@flexport/latitude-typescript";

import { datadogRum } from "@datadog/browser-rum";
try {
  datadogRum.init({
    applicationId: "3dd08c0c-42af-40d6-ba95-ca50f8f8d994",
    clientToken: "pubfe9d7032203026ccb8936b514bb4964f",
    site: "datadoghq.com",
    service: "file-uploader",
    env:
      window.location.hostname.includes("file-uploader.flexport.io") ||
      window.location.hostname.includes("file-uploader.flexport.com")
        ? "prod"
        : "dev",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    defaultPrivacyLevel: "mask-user-input",
  });
} catch (e) {
  console.error("Failed to initialize Datadog RUM", e);
}

const NavContainer = styled.nav`
  background-color: ${semanticColors.backgroundDark};
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }
`;

const NavLink = styled(Link)`
  color: ${semanticColors.textOnDark};
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const UserInfo = styled.div`
  color: ${semanticColors.textOnDark};
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
`;

const LogoutButton = styled.button`
  background: transparent;
  color: ${semanticColors.textOnDark};
  border: 1px solid ${semanticColors.borderMedium};
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  min-width: 90px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: ${semanticColors.borderLight};
  }
`;

const ContentContainer = styled.div`
  margin: 20px;
`;

const AccessPage = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const userId = searchParams.get("userId");
  const clientId = searchParams.get("clientId");

  useEffect(() => {
    console.log("[AccessPage] Processing access link", { userId, clientId });

    const run = async () => {
      if (!userId) {
        setError("No user ID provided in access link");
        return;
      }

      localStorage.setItem("userId", userId);
      if (clientId) {
        localStorage.setItem("clientId", clientId);
      }

      // Ensure we fetch CSRF once before trying to authenticate
      try {
        const backendPort = import.meta.env.VITE_BACKEND_PORT || "4000";
        const baseUrl =
          window.location.hostname === "localhost"
            ? `http://localhost:${backendPort}`
            : "";
        const resp = await fetch(`${baseUrl}/csrf-token`, {
          method: "GET",
          credentials: "include",
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data?.csrfToken) {
            localStorage.setItem("csrfToken", data.csrfToken);
            try {
              // Temporary fallback: set a non-HttpOnly cookie mirroring the token
              // to help some Chrome flows where cookie may be rejected before redeploy.
              document.cookie = `csrf-token=${data.csrfToken}; Path=/; SameSite=Lax`;
            } catch (e) {
              // ignore
            }
          }
        }
      } catch (e) {
        console.warn("[AccessPage] CSRF prefetch failed", e);
      }

      // Fetch user data to determine role-based routing
      getUserData()
        .then((userData) => {
          if (userData && userData.error) {
            // Handle authentication errors (invalid user ID, etc.)
            console.error("[AccessPage] Authentication error:", userData.error);
            localStorage.removeItem("userId");
            localStorage.removeItem("clientId");
            setError("User not found - invalid user ID provided");
            return;
          }

          if (userData && userData.role === "ADMIN") {
            window.location.href = "/admin";
          } else {
            window.location.href = "/files";
          }
        })
        .catch((error) => {
          console.error("[AccessPage] Error fetching user data", error);
          localStorage.removeItem("userId");
          localStorage.removeItem("clientId");
          setError("Error loading user data - please check your access link");
        });
    };

    run();
  }, [userId, clientId]);

  if (error) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h1 style={{ color: "#d32f2f" }}>Access Error</h1>
        <p>{error}</p>
      </div>
    );
  }

  return <div data-testid="auth-access-setup">Loading application...</div>;
};

const App: React.FC = () => {
  const [userData, setUserData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Log authentication state on app initialization
  useEffect(() => {
    const logAuthState = () => {
      const mlTokenLocal = localStorage.getItem("magicLinkSessionToken");
      const mlTokenSession = sessionStorage.getItem("magicLinkSessionToken");
      const userId = localStorage.getItem("userId");
      const hasCookies = document.cookie.includes("ml_session");

      console.log("[App] Authentication state on init:", {
        timestamp: new Date().toISOString(),
        hasLocalStorageToken: !!mlTokenLocal,
        hasSessionStorageToken: !!mlTokenSession,
        localStorageTokenLength: mlTokenLocal?.length || 0,
        sessionStorageTokenLength: mlTokenSession?.length || 0,
        userId: userId || "none",
        hasMlSessionCookie: hasCookies,
        cookiesEnabled: navigator.cookieEnabled,
        url: window.location.href,
        referrer: document.referrer,
      });

      // If we have sessionStorage token but not localStorage, restore it
      if (!mlTokenLocal && mlTokenSession) {
        console.warn(
          "[App] localStorage token missing but sessionStorage has it - restoring to localStorage",
        );
        try {
          localStorage.setItem("magicLinkSessionToken", mlTokenSession);
          console.log(
            "[App] ✓ Token restored to localStorage from sessionStorage",
          );
        } catch (error) {
          console.error(
            "[App] ✗ Failed to restore token to localStorage:",
            error,
          );
        }
      }

      // Restore userId from sessionStorage if missing in localStorage
      if (!userId) {
        const userIdSession = sessionStorage.getItem("userId");
        if (userIdSession) {
          console.warn(
            "[App] localStorage userId missing but sessionStorage has it - restoring to localStorage",
          );
          try {
            localStorage.setItem("userId", userIdSession);
            console.log(
              "[App] ✓ userId restored to localStorage from sessionStorage",
            );
          } catch (error) {
            console.error(
              "[App] ✗ Failed to restore userId to localStorage:",
              error,
            );
          }
        }
      }
    };

    logAuthState();
  }, []);

  const handleLogout = async (): Promise<void> => {
    // Notify the backend to invalidate the server-side session
    try {
      const backendPort = import.meta.env.VITE_BACKEND_PORT || "4000";
      const baseUrl =
        window.location.hostname === "localhost"
          ? `http://localhost:${backendPort}`
          : "";

      await fetch(`${baseUrl}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      // ignore errors, proceed with client cleanup
      console.warn("Logout request failed, continuing with client cleanup", e);
    }

    // Full logout: clear all auth artifacts including the magicLinkAuthType
    // flag so a future user on this browser starts fresh.
    await clearAuthStateOnLogout(client);

    // Navigate to logout success page
    window.location.href = "/logout-success";
  };

  useEffect(() => {
    // Unauthenticated routes: the user has no session yet, so GetUserData
    // would get a 401 and incorrectly trigger the session-expired modal.
    // We still need to fetch the CSRF token so mutations (e.g. RequestMagicLink,
    // ExchangeMagicLinkToken) have a valid CSRF header/cookie pair.
    const unauthenticatedRoutes = ["/access", "/auth/request"];
    const isUnauthenticatedRoute =
      unauthenticatedRoutes.includes(window.location.pathname) ||
      window.location.pathname.startsWith("/auth/magic-link/");

    // Perform sequential loading - first CSRF token, then user data
    const initializeApp = async () => {
      setIsLoading(true);

      // Step 1: Ensure we have a CSRF token
      try {
        const backendPort = import.meta.env.VITE_BACKEND_PORT || "4000";
        const baseUrl =
          window.location.hostname === "localhost"
            ? `http://localhost:${backendPort}`
            : "";
        const response = await fetch(`${baseUrl}/csrf-token`, {
          method: "GET",
          credentials: "include", // Important: include cookies
        });

        if (response.ok) {
          const data = await response.json();
          if (data.csrfToken) {
            localStorage.setItem("csrfToken", data.csrfToken);
            console.log("CSRF token obtained successfully");
          }
        }

        // Backfill the persistent magicLinkAuthType flag for users who logged in
        // before PR #428 introduced it. We check storage directly rather than
        // relying on the /csrf-token response (which runs without auth middleware
        // so req.auth is always null there and can't report authType).
        // This must run while the session is still active — before any 401 can
        // clear magicLinkSessionToken. The flag survives session expiry so the
        // modal still shows "Request New Link" after the token is cleared.
        const hasMlToken =
          !!localStorage.getItem("magicLinkSessionToken") ||
          !!sessionStorage.getItem("magicLinkSessionToken");
        const hasAuthTypeFlag = !!localStorage.getItem(
          MAGIC_LINK_AUTH_TYPE_KEY,
        );
        if (hasMlToken && !hasAuthTypeFlag) {
          localStorage.setItem(MAGIC_LINK_AUTH_TYPE_KEY, "magic_link");
          console.log(
            "[App] Backfilled magicLinkAuthType flag from session token presence",
          );
        }
      } catch (error) {
        console.error("Error fetching CSRF token:", error);
      }

      // Step 2: Load user data — skip on unauthenticated routes to avoid a
      // 401 from GetUserData triggering the session-expired modal prematurely.
      if (!isUnauthenticatedRoute) {
        try {
          // Short delay to ensure token is properly stored and available
          await new Promise((resolve) => setTimeout(resolve, 100));
          const data = await getUserData();
          setUserData(data);
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }

      setIsLoading(false);
    };

    initializeApp();
  }, []);

  const isAdmin = userData?.role === "ADMIN";
  const isNonAdmin = userData && userData.role !== "ADMIN";

  return (
    <ThemeProvider theme={THEME}>
      <GlobalStyle />
      <ApolloProvider client={client}>
        <NotificationProvider>
          <SessionExpiredProvider>
            <SessionWarningProvider>
              {isLoading ? (
                <div>Loading application...</div>
              ) : (
                <>
                  <NavContainer>
                    <NavLinks>
                      <LogoLink to={isAdmin ? "/admin" : "/files"}>
                        <Logo colorScheme="reversed" width={100} />
                      </LogoLink>
                      {isAdmin && (
                        <NavLink to="/admin">Admin Dashboard</NavLink>
                      )}
                      {isNonAdmin && (
                        <NavLink to="/files">File Uploader</NavLink>
                      )}
                    </NavLinks>
                    {userData && (
                      <UserInfo>
                        <LogoutButton
                          as={Link}
                          to="/login-history"
                          data-testid="nav-history-button"
                          aria-label="Login History"
                        >
                          History
                        </LogoutButton>
                        <LogoutButton
                          onClick={handleLogout}
                          data-testid="nav-logout-button"
                          aria-label="Logout"
                        >
                          Logout
                        </LogoutButton>
                        {userData.name}
                        {userData.clientName && ` | ${userData.clientName}`}
                      </UserInfo>
                    )}
                  </NavContainer>

                  <ContentContainer>
                    <Routes>
                      <Route path="/files" element={<FilesPage />} />
                      <Route
                        path="/login-history"
                        element={<LoginHistoryPage />}
                      />
                      <Route
                        path="/admin"
                        element={
                          userData?.role === "ADMIN" ? (
                            <ProtectedRoute
                              requiredRole="ADMIN"
                              userRole={userData?.role}
                              isAuthenticated={!!userData}
                            >
                              <AdminPage />
                            </ProtectedRoute>
                          ) : userData ? (
                            <Navigate to="/files" replace />
                          ) : (
                            <Navigate to="/" replace />
                          )
                        }
                      />
                      <Route path="/access" element={<AccessPage />} />
                      <Route
                        path="/logout-success"
                        element={
                          <div style={{ padding: "20px", textAlign: "center" }}>
                            <h1 style={{ color: "#fff" }}>
                              You have been logged out
                            </h1>
                            <p style={{ color: "#ddd" }}>
                              Your session has ended successfully.
                            </p>
                          </div>
                        }
                      />
                      <Route
                        path="/auth/magic-link/:token"
                        element={<MagicLinkAuthPage />}
                      />
                      <Route
                        path="/auth/request"
                        element={<RequestMagicLinkPage />}
                      />
                      <Route
                        path="/"
                        element={
                          isAdmin ? (
                            <Navigate to="/admin" replace />
                          ) : (
                            <Navigate to="/files" replace />
                          )
                        }
                      />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </ContentContainer>
                </>
              )}
            </SessionWarningProvider>
          </SessionExpiredProvider>
        </NotificationProvider>
      </ApolloProvider>
    </ThemeProvider>
  );
};

export default App;
